import React, { useState } from "react";
import { toast } from "sonner";
import { useLanguage, useT } from "@/lib/LanguageContext";
import { useAuth } from "@/lib/AuthContext";
import { crmErrorMessage } from "../api/crmErrors";
import { isValidEmail } from "../../../lib/validation";
import useTeam from "../hooks/useTeam";
import { fmtDayMonth } from "../format";
import CrmPageHeader from "../ui/CrmPageHeader";
import CrmCard from "../ui/CrmCard";
import CrmInput from "../ui/CrmInput";
import CrmButton from "../ui/CrmButton";
import CrmAlertDialog from "../ui/CrmAlertDialog";
import CrmLoadFailure from "../ui/CrmLoadFailure";
import StatusBadge from "../ui/StatusBadge";
import { LABEL } from "../ui/classes";
import { CRM } from "../../../constants/testIds/crm";

const STATUS_TONE = { active: "positive", invited: "warning" };

/**
 * Admin → CRM → Team (spec "Team", decision 5, P3): the owner invites a helper by email,
 * sees who has access and when they joined, resends a pending invite and removes access.
 * Owner-only route (`RoleGate action="team"` in crmRoutes.jsx) — a helper never reaches
 * this screen, so there is nothing here to hide per-row.
 */
export default function CrmTeam() {
  const t = useT("crm");
  const tc = useT("common");
  const { user } = useAuth();
  const { lang } = useLanguage();
  const { rows, loading, loadError, reload, invite, resend, remove } = useTeam();
  const [email, setEmail] = useState("");
  const [inviting, setInviting] = useState(false);
  const [resendingId, setResendingId] = useState(null);
  const [removeTarget, setRemoveTarget] = useState(null);
  const [removing, setRemoving] = useState(false);

  const submitInvite = async (e) => {
    e.preventDefault();
    const value = email.trim().toLowerCase();
    if (!isValidEmail(value) || inviting) return;
    setInviting(true);
    try {
      await invite(value);
      setEmail("");
      toast.success(t("team.invited"));
    } catch (err) {
      toast.error(crmErrorMessage(err, t, tc));
    } finally {
      setInviting(false);
    }
  };

  const doResend = async (row) => {
    setResendingId(row.id);
    try {
      await resend(row.id);
      toast.success(t("team.resendSent"));
    } catch (err) {
      toast.error(crmErrorMessage(err, t, tc));
    } finally {
      setResendingId(null);
    }
  };

  const doRemove = async () => {
    if (!removeTarget) return;
    setRemoving(true);
    try {
      await remove(removeTarget.id);
      toast.success(t("team.removed", { email: removeTarget.email }));
      setRemoveTarget(null);
    } catch (err) {
      toast.error(crmErrorMessage(err, t, tc));
    } finally {
      setRemoving(false);
    }
  };

  return (
    <div data-testid={CRM.team} className="space-y-4">
      <CrmPageHeader title={t("team.title")} subtitle={t("team.subtitle")} />
      {loadError && <CrmLoadFailure error={loadError} onRetry={reload} testId={CRM.teamError} />}

      <CrmCard>
        <form onSubmit={submitInvite} className="flex flex-wrap items-end gap-3">
          <label className="flex-1 min-w-[220px]">
            <span className={LABEL}>{t("team.inviteEmailLabel")}</span>
            <CrmInput
              type="email"
              value={email}
              placeholder={t("team.invitePlaceholder")}
              onChange={(e) => setEmail(e.target.value)}
              data-testid={CRM.teamInviteEmail}
            />
          </label>
          <CrmButton type="submit" disabled={!isValidEmail(email.trim()) || inviting} data-testid={CRM.teamInviteSubmit}>
            {t("team.inviteSubmit")}
          </CrmButton>
        </form>
      </CrmCard>

      <CrmCard className="divide-y divide-eh-border-soft p-0">
        <div className="flex items-center justify-between gap-3 px-5 py-4" data-testid={CRM.teamOwnerRow}>
          <div>
            <p className="text-sm font-medium text-eh-ink">{user?.email}</p>
            <p className="text-xs text-eh-muted">{t("team.ownerLabel")}</p>
          </div>
          <StatusBadge tone="positive">{t("team.ownerLabel")}</StatusBadge>
        </div>

        {!loading && rows.length === 0 && (
          <p className="px-5 py-4 text-sm text-eh-muted">{t("team.empty")}</p>
        )}

        {rows.map((row) => (
          <div key={row.id} className="flex flex-wrap items-center justify-between gap-3 px-5 py-4" data-testid={CRM.teamRow(row.id)}>
            <div>
              <p className="text-sm font-medium text-eh-ink">{row.email}</p>
              <p className="text-xs text-eh-muted">
                {row.status === "active" && row.accepted_at
                  ? t("team.acceptedOn", { date: fmtDayMonth(row.accepted_at, lang) })
                  : t("team.invitedOn", { date: fmtDayMonth(row.invited_at, lang) })}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <StatusBadge tone={STATUS_TONE[row.status] || "neutral"} testId={CRM.teamRowStatus(row.id)}>
                {t(row.status === "active" ? "enum.teamStatus.active" : "enum.teamStatus.invited")}
              </StatusBadge>
              {row.status === "invited" && (
                <CrmButton variant="outline" disabled={resendingId === row.id} onClick={() => doResend(row)} data-testid={CRM.teamRowResend(row.id)}>
                  {t("team.resend")}
                </CrmButton>
              )}
              <CrmButton variant="ghost" onClick={() => setRemoveTarget(row)} data-testid={CRM.teamRowRemove(row.id)}>
                {t("team.remove")}
              </CrmButton>
            </div>
          </div>
        ))}
      </CrmCard>

      {removeTarget && (
        <CrmAlertDialog
          open
          onOpenChange={(o) => { if (!o) setRemoveTarget(null); }}
          title={t("team.removeTitle", { email: removeTarget.email })}
          description={t("team.removeBody")}
          confirmLabel={t("team.remove")}
          tone="danger"
          busy={removing}
          onConfirm={doRemove}
          testId={CRM.teamRemoveDialog}
          confirmTestId={CRM.teamRemoveConfirm}
        />
      )}
    </div>
  );
}
